/**
 * Reference-frame transforms for placing a fragment from one structure onto another.
 *
 * Extracted from ConnectedDataCmds so the (combinatorial) frame logic can be unit-tested
 * without dragging in the app. Depends only on CaseData/structureKey and the pure
 * transform/math utilities.
 */
import CaseData from './CaseData';
import { structureKey } from './structureRef';
import { combineTransforms } from '../util/transform_utils';
import { InvertRTTransform } from '../math';

/**
 * The alignment frame a case defines: its prepared frame (otherProperties.alignedTo) when it has
 * one, else its structureKey. Used as BOTH the active query frame and the stored
 * alignReferenceFrame so they agree — otherwise the per-entry alignment cache misses every query.
 * @param {CaseData} caseData
 * @returns {string?}
 */
export function alignmentFrameKey(caseData) {
    return caseData?.mapCase?.otherProperties?.alignedTo || structureKey(caseData?.mapCase);
}

/**
 * Transform that re-expresses `mol`'s original coordinates in `targetCaseData`'s original
 * (unaligned) reference frame, so a fragment picked from one structure can be assembled in
 * another. Returns null when no transform is needed (mol already in the target's frame).
 * Throws when a cross-case transform is required but the structures share no alignment
 * reference (the loaded case). Independent of the UseAlignment display preference, since
 * these coordinates are sent for server-side assembly.
 * @param {*} mol
 * @param {CaseData} targetCaseData - the case the assembled compound will be added to
 * @returns {import('../util/transform_utils').Transform | null}
 * @throws {Error} when the mol has case data but doesn't share alignment ref with target case data
 */
export function assembleCompoundFrameTransform(mol, targetCaseData) {
    const atom = mol.getAtoms()[0];
    if (!atom || !targetCaseData) return null;

    const molCaseData = atom.getCaseData();

    // Loaded fragment (both mol and target have a CaseData).
    if (molCaseData) {
        // Same alignment frame -> identity, nothing to do. This covers mol === target AND two
        // *different* CaseDatas that were pre-aligned to the same `alignedTo` frame.
        // alignmentFrameKey() collapses a pre-aligned structure to its alignedTo frame, which
        // getTransformFromTo can't recognize on its own -- it only follows referenceCaseData,
        // not alignedTo -- so without this it could report co-framed structures as unrelated.
        if (alignmentFrameKey(molCaseData) === alignmentFrameKey(targetCaseData)) return null;
        const transform = CaseData.getTransformFromTo(molCaseData, targetCaseData);
        if (!transform) {
            // Genuinely unrelated frames (different frame keys and no shared reference) -> we
            // cannot express the fragment in the target frame. Fail fast instead of silently
            // sending source-frame coordinates, which would misplace the compound.
            throw new Error(
                `Can't place a fragment from ${molCaseData} onto ${targetCaseData}: the `
                + 'structures share no alignment reference. Align them and try again.'
            );
        }
        return transform;
    }

    // Fragment from an UNLOADED structure of interest / curated-panel member: no live
    // CaseData to give getTransformFromTo, only the source->reference alignment transform
    // carried on the decoded group (resolveCaseInfo, from ensureUnloadedAlignments). Compose
    // it with the target's reference->original. The Fragment Data Query flow aligns everything
    // to the active reference, so the carried alignment and the target share that reference.
    //
    // Unlike the loaded path we deliberately do NOT fail-fast on a missing transform: a null
    // molTransform legitimately means the fragment is already IN the reference frame (a
    // pre-aligned panel member, alignedTo the active frame == identity), which is the common
    // case, so we map it straight onto the target. The only unsound case is a regular SoI
    // whose alignment failed to compute upstream -- but that also breaks its 3D display, so it
    // surfaces before assembly, and it's indistinguishable here from the pre-aligned case.
    const molTransform = atom.getAtomTransform(); // src -> ref (null => already in ref frame)
    const targetTransform = targetCaseData.getWholeProteinTransform(); // target -> reference
    if (!molTransform) return targetTransform ? InvertRTTransform(targetTransform) : null;
    if (!targetTransform) return molTransform; // target is the reference frame
    return combineTransforms(molTransform, InvertRTTransform(targetTransform));
}
